import React, { useEffect, useState } from "react";
import { collection, getDoc, getDocs } from "firebase/firestore";
import { httpsCallable } from "firebase/functions";
import { Button, Modal } from "react-bootstrap";
import { db, functions } from "../../../firebase";
import { useAuth } from "../../../context/AuthContext";
import { buildCustomerAccessField, createCustomerAccessDraft, getNodeCustomerAccess } from "../../../utils/staffCustomerAccess";
import { notifyError, notifyTopRight } from "../toast/SetToast";
import { annualMaskSenderConfigRef, getWorkflowSenders, normalizeSenderEmail, saveAnnualMaskSenderProfile } from "../Followup/annualMaskEmailSender";
import AdminNodeSelect from "./AdminNodeSelect";
import AdminCustomerAccessSelect from "./AdminCustomerAccessSelect";
import AnnualMaskSenderConnectionControls from "./AnnualMaskSenderConnectionControls";

const AnnualMaskNodeSenderConfig = ({ workflow = "annual-mask" }) => {
  const { currentUser } = useAuth();
  const isSuperAdmin = Boolean(currentUser && Array.isArray(currentUser.roles) && currentUser.roles.includes("super_admin"));
  const workflowTitle = workflow === "booking" ? "Booking Calendar — Doctor Emails" : "CPAP Follow-Up — Annual Mask";
  const inputPrefix = `${workflow}-sender`;
  const [nodes, setNodes] = useState([]);
  const [senders, setSenders] = useState([]);
  const [email, setEmail] = useState("");
  const [selectedNodes, setSelectedNodes] = useState([]);
  const [customerDraft, setCustomerDraft] = useState({});
  const [originalEmail, setOriginalEmail] = useState("");
  const [pendingRemoval, setPendingRemoval] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    let active = true;
    if (!isSuperAdmin) return undefined;
    const load = async () => {
      try {
        const [nodeSnapshot, settings] = await Promise.all([getDocs(collection(db, "nodes")), getDoc(annualMaskSenderConfigRef(db))]);
        const rows = await Promise.all(nodeSnapshot.docs.map(async (row) => {
          const customers = await getDocs(collection(db, "nodes", row.id, "customers"));
          return { ...row.data(), id: row.id, customers: customers.docs.map((customer) => ({ ...customer.data(), id: customer.id })) };
        }));
        if (!active) return;
        setNodes(rows);
        setSenders(getWorkflowSenders(settings.exists() ? settings.data() : {}, workflow));
      } catch (loadError) {
        if (active) { setError(true); notifyError("Unable to load sender settings. Reload the page to try again."); }
      } finally { if (active) setLoading(false); }
    };
    load();
    return () => { active = false; };
  }, [isSuperAdmin, workflow]);

  const reset = () => { setEmail(""); setSelectedNodes([]); setCustomerDraft({}); setOriginalEmail(""); };
  const save = async (event) => {
    event.preventDefault();
    if (!isSuperAdmin || saving || loading || error) return;
    setSaving(true);
    try {
      const profile = { email: normalizeSenderEmail(email), node: selectedNodes };
      const customerAccess = buildCustomerAccessField(customerDraft, selectedNodes);
      if (customerAccess !== null) profile.customerAccess = customerAccess;
      setSenders(await saveAnnualMaskSenderProfile(db, originalEmail, profile, workflow));
      reset();
      notifyTopRight(`${workflowTitle} sender assignments saved.`);
    } catch (saveError) { notifyError(saveError.message || "Unable to save sender settings."); }
    finally { setSaving(false); }
  };
  const remove = async () => {
    if (!isSuperAdmin || !pendingRemoval || saving) return;
    setSaving(true);
    try {
      const result = await httpsCallable(functions, "disconnectAnnualMaskSender")({ email: pendingRemoval, removeAssignments: true, workflow });
      if (!Array.isArray(result.data.senders)) throw new Error("Mailbox disconnected, but removal requires the updated email backend. Please try again after deployment.");
      setSenders(result.data.senders);
      if (originalEmail === pendingRemoval) reset();
      setPendingRemoval("");
      notifyTopRight(result.data.mailboxRetained
        ? "Sender removed from this workflow. Its mailbox stays connected for the other workflow."
        : "Sender removed from this workflow and mailbox disconnected.");
    } catch (removeError) { notifyError(removeError.message || "Unable to remove sender."); }
    finally { setSaving(false); }
  };

  if (!isSuperAdmin) return null;
  return (
    <div className="card">
      <div className="card-header"><h4 className="card-title mb-0">{workflowTitle}</h4></div>
      <div className="card-body">
        <p className="text-muted mb-2">{workflow === "booking"
          ? "Choose who sends doctor review emails from Booking Calendar."
          : "Choose who sends annual mask doctor emails from CPAP Follow-Up and Annual Mask Batch Review."} Assign sender addresses by node and customer.</p>
        <p className="small text-muted">Assignments apply only to this workflow. After saving, connect the Google mailbox. Sleepcare (Firebase default) sends emails for cases without a matching assignment.</p>
        {loading ? <div className="text-muted">Loading sender settings…</div> : error ? (
          <div className="text-danger">Sender settings could not be loaded. Reload the page to try again.</div>
        ) : (
          <>
            <form onSubmit={save} className="mb-4">
              <fieldset disabled={saving}>
                <div style={saving ? { pointerEvents: "none", opacity: 0.7 } : undefined}>
                  <div className="form-group"><label htmlFor={`${inputPrefix}-email`}>Sender email</label>
                    <input id={`${inputPrefix}-email`} type="email" required className="form-control" placeholder="name@example.com" value={email} onChange={(event) => setEmail(event.target.value)} />
                  </div>
                  <div className="form-group"><label htmlFor={`${inputPrefix}-nodes`}>Nodes</label>
                    <AdminNodeSelect nodesList={nodes} value={selectedNodes} onChange={setSelectedNodes} inputId={`${inputPrefix}-nodes`} />
                  </div>
                  <AdminCustomerAccessSelect nodesList={nodes} selectedNodeCodes={selectedNodes} value={customerDraft} onChange={setCustomerDraft}
                    title="Customers for this sender" description="Use this email for all customers or selected customers under each node." badgeLabel="Email sender" />
                </div>
                <Button type="submit" className="mt-3 mr-2">{saving ? "Saving…" : originalEmail ? "Save changes" : "Add sender"}</Button>
                <Button type="button" variant="light" className="mt-3" onClick={reset}>{originalEmail ? "Cancel edit" : "Clear"}</Button>
              </fieldset>
            </form>
            {senders.length ? <div className="table-responsive"><table className="table table-sm">
              <thead><tr><th>Sender email</th><th>Nodes / customers</th><th>Mailbox connection</th><th /></tr></thead>
              <tbody>{senders.map((sender) => <tr key={sender.email}>
                <td>{sender.email}</td>
                <td>{sender.node.map((code) => {
                  const node = nodes.find((row) => row.nodecode === code);
                  const scope = getNodeCustomerAccess(sender, code);
                  const names = scope.customerIds.map((id) => {
                    const customer = node && node.customers.find((row) => row.id === id);
                    return customer ? customer.CustomerNameTH || customer.CustomerNameEN || id : id;
                  });
                  return <div key={code}><strong>{code}</strong> · {scope.mode === "all" ? "All customers" : names.join(", ")}</div>;
                })}</td>
                <td><AnnualMaskSenderConnectionControls email={sender.email} disabled={saving} /></td>
                <td><Button size="sm" variant="outline-primary" className="mr-2" disabled={saving} onClick={() => {
                  setOriginalEmail(sender.email); setEmail(sender.email); setSelectedNodes(sender.node); setCustomerDraft(createCustomerAccessDraft(sender, sender.node));
                }}>Edit</Button><Button size="sm" variant="outline-danger" disabled={saving} onClick={() => setPendingRemoval(sender.email)}>Remove</Button></td>
              </tr>)}</tbody>
            </table></div> : <div className="text-muted">No sender assignments for this workflow. All nodes use Firebase default.</div>}
          </>
        )}
      </div>
      <Modal centered show={Boolean(pendingRemoval)} onHide={() => { if (!saving) setPendingRemoval(""); }}>
        <Modal.Header closeButton={!saving}><Modal.Title>Remove sender from this workflow?</Modal.Title></Modal.Header>
        <Modal.Body>Remove <strong>{pendingRemoval}</strong> from {workflowTitle}? Its cases in this workflow will use Firebase default. The mailbox stays connected if the other workflow uses it; otherwise it will be disconnected.</Modal.Body>
        <Modal.Footer><Button variant="light" disabled={saving} onClick={() => setPendingRemoval("")}>Cancel</Button><Button variant="danger" disabled={saving} onClick={remove}>Remove sender</Button></Modal.Footer>
      </Modal>
    </div>
  );
};
export default AnnualMaskNodeSenderConfig;
